import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Copy, ExternalLink, BarChart3, Users, User } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, shortUrl } from "@/lib/api";
import { PageHeader, Panel, PrimaryButton } from "@/components/dash/Ui";
import { LinkDialog } from "@/components/dash/LinkDialog";

const iconBtn = "p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors";
const EMPTY = { title: "", slug: "", message: "", mode: "single", numbers: [{ name: "", phone: "" }], active: true };

export default function Links() {
  const [links, setLinks] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/links").then((r) => setLinks(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/links/${form.id}`, form);
      else await api.post("/links", form);
      toast.success(form.id ? "Link updated" : "Link generated");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (l) => { await api.put(`/links/${l.id}`, { ...l, active: !l.active }); load(); };
  const remove = async (l) => {
    if (!window.confirm(`Delete /${l.slug}?`)) return;
    await api.delete(`/links/${l.id}`);
    load();
  };
  const copy = (slug) => { navigator.clipboard.writeText(shortUrl(slug)); toast.success("Copied"); };

  return (
    <div>
      <PageHeader eyebrow="WhatsApp links" title="Your links">
        <PrimaryButton data-testid="add-link-button" onClick={() => setEdit(EMPTY)}>
          <Plus className="w-4 h-4" /> Generate link
        </PrimaryButton>
      </PageHeader>
      <div className="space-y-3">
        {links.map((l) => (
          <Panel key={l.id} data-testid={`link-row-${l.slug}`} className="flex flex-wrap items-center gap-6 hover:border-slate-700 transition-colors">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2">
                <h3 className="font-display font-semibold text-lg">{l.title}</h3>
                <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider rounded-full border border-slate-700 px-2 py-0.5 text-slate-400">
                  {l.mode === "multi" ? <Users className="w-3 h-3" /> : <User className="w-3 h-3" />} {l.mode === "multi" ? "multi-agent" : "single"}
                </span>
              </div>
              <p className="font-mono text-sm text-emerald-400 mt-1" data-testid={`link-url-${l.slug}`}>{shortUrl(l.slug)}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {l.numbers.map((n, i) => (
                  <span key={i} className="text-xs rounded-full bg-slate-800 px-2.5 py-1 text-slate-300 font-mono">
                    {n.name ? `${n.name} · ` : ""}+{n.phone}
                  </span>
                ))}
              </div>
            </div>
            <Link to={`/dashboard/links/${l.id}`} data-testid={`link-stats-${l.slug}`} className="text-center group">
              <p className="font-display text-2xl font-bold group-hover:text-emerald-400 transition-colors" data-testid={`link-clicks-${l.slug}`}>{l.clicks}</p>
              <p className="text-xs text-slate-500">clicks</p>
            </Link>
            <Switch data-testid={`link-active-switch-${l.slug}`} checked={l.active} onCheckedChange={() => toggle(l)} />
            <div className="flex gap-1">
              <Link to={`/dashboard/links/${l.id}`} data-testid={`link-analytics-${l.slug}`} className={iconBtn}><BarChart3 className="w-4 h-4" /></Link>
              <button data-testid={`link-copy-${l.slug}`} onClick={() => copy(l.slug)} className={iconBtn}><Copy className="w-4 h-4" /></button>
              <a data-testid={`link-open-${l.slug}`} href={`/${l.slug}`} target="_blank" rel="noreferrer" className={iconBtn}><ExternalLink className="w-4 h-4" /></a>
              <button data-testid={`link-edit-${l.slug}`} onClick={() => setEdit(l)} className={iconBtn}><Pencil className="w-4 h-4" /></button>
              <button data-testid={`link-delete-${l.slug}`} onClick={() => remove(l)} className={`${iconBtn} hover:text-red-400`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </Panel>
        ))}
        {!links.length && <Panel className="text-center text-slate-500 py-16" data-testid="links-empty">No links yet. Generate your first WhatsApp link.</Panel>}
      </div>
      <LinkDialog value={edit} onClose={() => setEdit(null)} onSave={save} />
    </div>
  );
}
